import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { DataService } from 'src/app/data.service';

/**
 *  ENTRA-ÖSSZERENDELÉS – melyik tanuló lépett be a címtárból.
 *
 *  Csak TANULÓRA: a dolgozónak az azonosítója a belépési kulcsa, azon nincs mit
 *  összerendelni. A tanulónál viszont a KIR nem ad e-mail címet, a belépés pedig épp
 *  arra illeszt – a címtárbeli cím beírása teremti meg a kapcsolatot.
 *
 *  A javaslatot a rendszer adja, a DÖNTÉS a felhasználóé: minden sor kézzel
 *  felülbírálható, mert egy rossz összerendelés azt jelentené, hogy valaki más
 *  adatait látja a belépő. Ezért csak EGYÉRTELMŰ találatot javaslunk – ahol több
 *  azonos nevű ember van, ott inkább nem választunk.
 */
@Component({
  selector: 'app-entra-parositas',
  templateUrl: './entra-parositas.component.html',
  styleUrls: ['./entra-parositas.component.scss'],
})
export class EntraParositasComponent implements OnInit {
  intezmeny = '';
  toltodik = false;
  hiba = '';

  /* címtár */
  entraSorok: any[] = [];
  entraFut = false;
  entraForras = '';
  graphCsoport = '';

  /* saját tanulók */
  tanulok: any[] = [];
  szuro = '';
  csakHianyzo = true;

  /** azonosító → { belepes, alap } – amit menteni fogunk. */
  valasztas: { [azonosito: string]: { belepes: string; alap: string } } = {};
  mentesFut = false;
  eredmeny: any = null;

  /* tömeges pótlás (kiszolgálói döntés, szigorúbb szabállyal) */
  potlas: any = null;
  potlasFut = false;

  constructor(private http: HttpClient, private data: DataService) {}

  async ngOnInit() {
    this.intezmeny = (await this.data.getFromStorage('sajatIntezmeny'))?.intezmenyId || '';
    await this.betolt();
  }

  async betolt() {
    if (!this.intezmeny) return;
    this.toltodik = true;
    this.hiba = '';
    try {
      const r: any = await firstValueFrom(
        this.http.get(`/api/import/parositando/${this.intezmeny}`));
      this.tanulok = r?.tanulok || [];
      this.javaslatok();
    } catch (e: any) {
      this.hiba = e?.error?.error || 'A tanulók lekérése nem sikerült.';
    } finally {
      this.toltodik = false;
    }
  }

  /* ── címtár ─────────────────────────────────────────────────────────── */

  async graphLekeres() {
    this.entraFut = true;
    this.hiba = '';
    try {
      const p = this.graphCsoport ? `?csoport=${encodeURIComponent(this.graphCsoport)}` : '';
      const r: any = await firstValueFrom(this.http.get(
        `/api/import/entra-felhasznalok/${this.intezmeny}${p}`));
      this.entraSorok = r?.sorok || [];
      this.entraForras = `Microsoft Graph – ${r?.db ?? 0} fiók`
        + (r?.csonkolt ? ' (csonkolva, szűkíts csoportra)' : '');
      this.javaslatok();
    } catch (e: any) {
      this.hiba = e?.error?.error || 'A címtár lekérése nem sikerült.';
    } finally {
      this.entraFut = false;
    }
  }

  /* ── névillesztés ───────────────────────────────────────────────────── */

  private jelek(...reszek: any[]): Set<string> {
    const nyers = reszek.map((r) => String(r ?? '').trim()).filter(Boolean).join(' ');
    const tiszta = nyers.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
    return new Set(tiszta.split(/[^a-z0-9]+/).filter((x) => x && x !== 'dr'));
  }
  private egyezik(a: Set<string>, b: Set<string>) {
    return a.size === b.size && [...a].every((x) => b.has(x));
  }
  private resze(kicsi: Set<string>, nagy: Set<string>) {
    return kicsi.size > 0 && [...kicsi].every((x) => nagy.has(x));
  }

  /** A címtárfiók belépési neve – ezt írjuk a felhasználóhoz. */
  belepesiNev(e: any): string {
    return String(e?.userPrincipalName || e?.mail || '').trim();
  }

  entraCimke(e: any): string {
    const nev = String(e?.displayName || '').trim();
    const be = this.belepesiNev(e);
    return nev && be ? `${nev} — ${be}` : (nev || be);
  }

  /**
   *  Javaslat minden felhasználóra: előbb a MEGLÉVŐ érték (ha már van), utána a
   *  címtárból névre illesztve. Csak egyértelmű találatot fogadunk el.
   */
  javaslatok() {
    if (!this.entraSorok.length) return;
    const index = this.entraSorok
      .map((e) => ({
        e,
        keszletek: [this.jelek(e.displayName), this.jelek(e.surname, e.givenName)]
          .filter((k) => k.size >= 2),
      }))
      .filter((x) => x.keszletek.length && this.belepesiNev(x.e));

    const talal = (j: Set<string>) => {
      if (!j.size) return null;
      const pontos = index.filter((x) => x.keszletek.some((k) => this.egyezik(j, k)));
      if (pontos.length === 1) return { e: pontos[0].e, alap: 'név (pontos)' };
      if (pontos.length > 1) return { e: null, alap: 'több azonos nevű' };
      const resz = index.filter((x) => x.keszletek.some((k) => this.resze(j, k)));
      if (resz.length === 1) return { e: resz[0].e, alap: 'név (részleges)' };
      return { e: null, alap: resz.length ? 'több azonos nevű' : '' };
    };

    for (const f of this.tanulok) {
      const meglevo = f.email || '';
      if (meglevo) { f.javaslat = meglevo; f.javaslatAlap = 'már beállítva'; continue; }
      const r = talal(this.jelek(f.viselt_vez, f.viselt_ker));
      f.javaslat = r?.e ? this.belepesiNev(r.e) : '';
      f.javaslatAlap = r?.alap || 'nincs találat';
    }
  }

  /* ── lista / szerkesztés ────────────────────────────────────────────── */

  get lista(): any[] {
    const sz = this.szuro.trim().toLowerCase();
    return this.tanulok.filter((f) => {
      const jelenlegi = this.ertek(f);
      if (this.csakHianyzo && jelenlegi) return false;
      if (!sz) return true;
      return `${f.nev} ${f.azonosito} ${jelenlegi}`.toLowerCase().includes(sz);
    });
  }

  /** A sor aktuális értéke: a kézi választás, különben a meglévő, különben a javaslat. */
  ertek(f: any): string {
    const v = this.valasztas[f.azonosito];
    if (v) return v.belepes;
    return f.email || '';
  }
  alap(f: any): string {
    return this.valasztas[f.azonosito]?.alap || f.javaslatAlap || '';
  }
  valtozott(f: any): boolean {
    const eredeti = f.email || '';
    return this.ertek(f) !== eredeti;
  }

  allit(f: any, ertek: string, alap = 'kézi') {
    this.valasztas[f.azonosito] = { belepes: String(ertek || '').trim(), alap };
  }
  visszavon(f: any) { delete this.valasztas[f.azonosito]; }

  /** A javaslatok elfogadása a most látható sorokra. */
  javaslatokatElfogad() {
    let db = 0;
    for (const f of this.lista) {
      if (!f.javaslat || this.ertek(f)) continue;
      this.allit(f, f.javaslat, f.javaslatAlap);
      db++;
    }
    this.data.sendToast(db ? `${db} javaslat elfogadva – mentés még kell.` : 'Nincs elfogadható javaslat.',
      db ? 'success' : 'warning');
  }

  get valtozasok(): any[] {
    return this.tanulok.filter((f) => this.valtozott(f));
  }

  /**
   *  Ugyanaz a cím két tanulónál: a szerver visszautasítja, de jobb, ha itt látszik.
   *  A belépés az e-mailre illeszt, tehát a duplikátum azt jelentené, hogy a belépő
   *  kiszámíthatatlanul valaki más adatait kapja.
   */
  utkozik(f: any): boolean {
    const cim = this.ertek(f).trim().toLowerCase();
    if (!cim) return false;
    return this.tanulok.some((m) => m !== f && this.ertek(m).trim().toLowerCase() === cim);
  }
  get utkozok(): number {
    return this.tanulok.filter((f) => this.utkozik(f)).length;
  }

  /* ── hiányzó címek tömeges kitöltése ─────────────────────────────────── */

  /**
   *  A KISZOLGÁLÓ dönt, és szigorúbban, mint a soronkénti javaslat: oktatási azonosító,
   *  vagy pontos és egyértelmű név (több azonos névnél az osztály dönt). Részleges
   *  névegyezést itt nem fogad el, és csak üres mezőbe ír. Előbb mindig próbafutás:
   *  a felhasználó látja a listát, és csak utána ír.
   */
  async potlasProba() {
    this.potlasFut = true;
    this.hiba = '';
    try {
      this.potlas = await firstValueFrom(this.http.post('/api/import/entra-email-potlas', {
        intezmeny: this.intezmeny, csoport: this.graphCsoport || undefined, probafutas: true,
      }));
    } catch (e: any) {
      this.hiba = e?.error?.error || 'A címtár-összevetés nem sikerült.';
    } finally {
      this.potlasFut = false;
    }
  }

  async potlasIras() {
    const db = this.potlas?.irhato_db || 0;
    if (!db) return;
    if (!confirm(`${db} tanulónál kitöltjük az e-mail címet a címtárból. A cím a belépés kulcsa – `
      + 'a listát átnézted? Folytatod?')) return;
    this.potlasFut = true;
    try {
      const r: any = await firstValueFrom(this.http.post('/api/import/entra-email-potlas', {
        intezmeny: this.intezmeny, csoport: this.graphCsoport || undefined, probafutas: false,
      }));
      this.data.sendToast(`Kitöltve: ${r?.irt ?? 0} tanuló.`, 'success');
      this.potlas = r;
      await this.betolt();
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A kitöltés nem sikerült.', 'error');
    } finally {
      this.potlasFut = false;
    }
  }

  async ment() {
    const tetelek = this.valtozasok.map((f) => ({
      azonosito: f.azonosito, belepes: this.ertek(f),
    }));
    if (!tetelek.length) { this.data.sendToast('Nincs mentendő változás.', 'warning'); return; }
    const torles = tetelek.filter((t) => !t.belepes).length;
    // Az e-mail EGYBEN a belépés kulcsa – a törlés kizárja a tanulót.
    if (torles && !confirm(`${torles} összerendelést TÖRÖLNI fogsz: ezek a tanulók `
      + 'ezután nem tudnak belépni. Folytatod?')) return;
    this.mentesFut = true;
    try {
      this.eredmeny = await firstValueFrom(
        this.http.post('/api/import/entra-parositas', { intezmeny: this.intezmeny, tetelek }));
      const hibaDb = this.eredmeny?.hibak?.length || 0;
      this.data.sendToast(`Mentve: ${this.eredmeny?.tanulo ?? 0} tanuló.`
        + (hibaDb ? ` ${hibaDb} sor kimaradt – lásd a listát.` : ''), hibaDb ? 'warning' : 'success');
      this.valasztas = {};
      await this.betolt();
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A mentés nem sikerült.', 'error');
    } finally {
      this.mentesFut = false;
    }
  }
}
